import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tab } from 'bootstrap';

import { UseOutletContextCompany } from './Company';
import { StateLanguage } from '../../../languages/config/StateLanguage';

import AppCompanyUpdateInformation from './component/CompanyUpdateInformation';
import AppCompanyUpdateGeneral from './component/CompanyUpdateGeneral';
import AppCompanyUpdateDownload from './component/CompanyUpdateDownload';
import AppCompanyUpdateAccounting from './component/CompanyUpdateAccounting';
import AppCompanyUpdatePayroll from './component/CompanyUpdatePayroll';
import { useSubscription } from '../../../contexts/SubscriptionContext';

export interface CompanyUpdatePageProps {};

const baseTabMap: Record<string, string> = {
  info: '#container-information',
  general: '#container-settings-general',
  accounting: '#container-settings-accounting',
  download: '#container-settings-download',
}

function getTabMap(canPayroll: boolean): Record<string, string> {
  if (canPayroll) {
    return { ...baseTabMap, salary: '#container-settings-salary' }
  }
  return baseTabMap
}

const CompanyUpdatePage: React.FunctionComponent<CompanyUpdatePageProps> = props => {
  const {setRoute} = UseOutletContextCompany()
  const {lang} = StateLanguage()
  const [searchParams] = useSearchParams()
  const { can: canSub } = useSubscription()
  const canPayroll = canSub('read', 'digital:payroll:*').allowed

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.companies, branch: lang.labels.editCompany}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!mounted) return

    const tab = searchParams.get('tab')
    const tabMap = getTabMap(canPayroll)
    if (!tab || !tabMap[tab]) return

    let attempts = 0
    const maxAttempts = 40

    const intervalId = setInterval(() => {
      attempts++
      const navLink = document.querySelector(`a[data-bs-toggle="tab"][href="${tabMap[tab]}"]`) as HTMLElement | null

      if (navLink) {
        clearInterval(intervalId)
        try {
          Tab.getOrCreateInstance(navLink).show()
        } catch {
          navLink.click()
        }
      } else if (attempts >= maxAttempts) {
        clearInterval(intervalId)
      }
    }, 50)

    return () => clearInterval(intervalId)
  }, [searchParams, mounted])

  if (!mounted) return null

  return (
    <div className="tab-content">
      <div id="container-information" className="tab-pane fade show active" role="tabpanel">
        <AppCompanyUpdateInformation></AppCompanyUpdateInformation>
      </div>
      <div id="container-settings-general" className="tab-pane fade" role="tabpanel">
        <AppCompanyUpdateGeneral></AppCompanyUpdateGeneral>
      </div>
      <div id="container-settings-accounting" className="tab-pane fade" role="tabpanel">
        <AppCompanyUpdateAccounting></AppCompanyUpdateAccounting>
      </div>
      <div id="container-settings-download" className="tab-pane fade" role="tabpanel">
        <AppCompanyUpdateDownload></AppCompanyUpdateDownload>
      </div>
      {canPayroll && (
        <div id="container-settings-salary" className="tab-pane fade" role="tabpanel">
          <AppCompanyUpdatePayroll></AppCompanyUpdatePayroll>
        </div>
      )}
    </div>
  )
};

export default CompanyUpdatePage;
